import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Typography,
  Skeleton,
  Stack,
  Button,
  Card,
  CardContent,
  Divider,
  useTheme,
  alpha,
  useMediaQuery,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { tokens } from "../../theme";

const StandardTable = ({
  columns,
  data = [],
  loading = false,
  error = null,
  onRetry,
  page = 1,
  count = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  rowAction,
  enableColumnManagement = false, // The new prop to control the feature
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const muiPage = page > 0 ? page - 1 : 0;

  const headerBgColor =
    theme.palette.mode === "dark"
      ? colors.primary[600]
      : theme.palette.grey[100];

  const headerTextColor =
    theme.palette.mode === "dark"
      ? colors.grey[100]
      : theme.palette.primary.main;

  // --- COLUMN MANAGEMENT STATE ---
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumnIds, setVisibleColumnIds] = useState([]);

  // Initialize visible columns based on `defaultHidden` property
  useEffect(() => {
    const defaultVisible = columns
      .filter((c) => !c.defaultHidden)
      .map((c) => c.id);
    setVisibleColumnIds(defaultVisible);
  }, [columns]); // Re-initialize if the master column list ever changes

  const handleColumnsClick = (event) => setAnchorEl(event.currentTarget);
  const handleColumnsClose = () => setAnchorEl(null);

  const toggleColumnVisibility = (columnId) => {
    setVisibleColumnIds(
      (prevIds) =>
        prevIds.includes(columnId)
          ? prevIds.filter((id) => id !== columnId) // Remove it
          : [...prevIds, columnId], // Add it
    );
  };

  // Derive the columns to actually render based on the current state
  const visibleColumns = columns.filter((col) =>
    visibleColumnIds.includes(col.id),
  );

  const handleChangePage = (event, newPage) => onPageChange(newPage + 1);
  const handleChangeRowsPerPage = (event) =>
    onRowsPerPageChange(parseInt(event.target.value, 10));

  // --- LOADING STATE ---
  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        {[...Array(isMobile ? 3 : 5)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={isMobile ? 150 : 50}
            sx={{ mb: 1, borderRadius: theme.appRadius?.md || 1 }}
          />
        ))}
      </Box>
    );
  }

  // --- ERROR STATE ---
  if (error) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: theme.appRadius?.md || 1,
          border: `1px dashed ${theme.palette.error.main}`,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
            }}
          >
            <ErrorOutlineIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" color="text.primary">
            Unable to load data
          </Typography>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
          {onRetry && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              Try Again
            </Button>
          )}
        </Stack>
      </Box>
    );
  }

  // --- EMPTY STATE ---
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
          bgcolor: "background.paper",
          borderRadius: theme.appRadius?.md || 1,
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No records found
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Try adjusting your filters.
        </Typography>
      </Box>
    );
  }

  // --- TABLE HEADER TOOLBAR (For Desktop Column Toggles) ---
  const renderTableToolbar = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        px: 2,
        py: 1,
        bgcolor: headerBgColor,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      {enableColumnManagement && !isMobile && (
        <>
          <Tooltip title="Manage Columns">
            <IconButton onClick={handleColumnsClick} size="small">
              <ViewColumnIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleColumnsClose}
          >
            <Typography
              variant="overline"
              sx={{ px: 2, pb: 1, color: "text.secondary" }}
            >
              Visible Columns
            </Typography>
            {/* The menu maps over the *master* columns list */}
            {columns.map((col) => (
              <MenuItem
                key={col.id}
                dense
                onClick={() => toggleColumnVisibility(col.id)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={visibleColumnIds.includes(col.id)}
                      size="small"
                      color="secondary"
                    />
                  }
                  label={<Typography variant="body2">{col.label}</Typography>}
                  sx={{ m: 0, pointerEvents: "none" }}
                />
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );

  // --- MOBILE VIEW (CARDS) ---
  if (isMobile) {
    return (
      <Box>
        <Stack spacing={2}>
          {data.map((row, index) => (
            <Card
              key={row.id || index}
              elevation={2}
              sx={{
                bgcolor: "background.paper",
                borderRadius: theme.appRadius?.md || 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                {/* Mobile view now respects the user's choice */}
                {visibleColumns.map((col) => {
                  const value = row[col.id];
                  return (
                    <Box
                      key={col.id}
                      sx={{
                        mb: 1.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{ textTransform: "uppercase", mr: 2, mt: 0.5 }}
                      >
                        {col.label}
                      </Typography>
                      <Box sx={{ textAlign: "right", maxWidth: "60%" }}>
                        {col.format ? (
                          col.format(value, row)
                        ) : (
                          <Typography variant="body2" fontWeight={500}>
                            {value}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {rowAction && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {rowAction(row)}
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
        <TablePagination
          rowsPerPageOptions={[10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={muiPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 2, border: "none", bgcolor: "transparent" }}
        />
      </Box>
    );
  }

  // --- DESKTOP VIEW (TABLE) ---
  return (
    <Paper
      elevation={2}
      sx={{
        width: "100%",
        overflow: "hidden",
        borderRadius: theme.appRadius?.lg || 1,
      }}
    >
      {renderTableToolbar()}
      <TableContainer
        sx={{
          maxHeight: "calc(100vh - 300px)",
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Table
          stickyHeader
          sx={{ "& .MuiTableCell-root": { borderBottom: "none" } }}
        >
          <TableHead>
            <TableRow>
              {/* Desktop view also respects the user's choice */}
              {visibleColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "left"}
                  style={{ minWidth: column.minWidth }}
                  sx={{
                    bgcolor: headerBgColor, // Unified Color
                    color: headerTextColor,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {rowAction && (
                <TableCell
                  align="center"
                  sx={{
                    bgcolor: headerBgColor, // Unified Color
                    color: headerTextColor,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    fontSize: "0.75rem",
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                {visibleColumns.map((column) => {
                  const value = row[column.id];
                  return (
                    <TableCell key={column.id} align={column.align}>
                      {column.format ? column.format(value, row) : value}
                    </TableCell>
                  );
                })}
                {rowAction && (
                  <TableCell align="center">{rowAction(row)}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={muiPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "inherit",
        }}
      />
    </Paper>
  );
};

export default StandardTable;
